import { ShowcasePalette } from "../../integrations/clientConfig";

export type PalettePreset = {
  id: string;
  name: string;
  description: string;
  tone: "dark" | "light";
  palette: ShowcasePalette;
};

const rgba = (r: number, g: number, b: number, alpha: number) =>
  `rgba(${r}, ${g}, ${b}, ${alpha})`;

const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

const toHex = (value: number) => clamp(value).toString(16).padStart(2, "0");

const rgbToHex = (r: number, g: number, b: number) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

const mix = (
  colorA: [number, number, number],
  colorB: [number, number, number],
  weight = 0.5,
): [number, number, number] => [
  clamp(colorA[0] * (1 - weight) + colorB[0] * weight),
  clamp(colorA[1] * (1 - weight) + colorB[1] * weight),
  clamp(colorA[2] * (1 - weight) + colorB[2] * weight),
];

const lighten = (color: [number, number, number], amount: number) =>
  mix(color, [255, 255, 255], amount);

const darken = (color: [number, number, number], amount: number) =>
  mix(color, [0, 0, 0], amount);

export const createDarkPaletteFromAccent = (
  accentColor: [number, number, number],
): ShowcasePalette => {
  const accentStrong = lighten(accentColor, 0.12);
  const badge = lighten(accentColor, 0.08);
  const pageStart = darken(accentColor, 0.94);
  const pageCenter = darken(accentColor, 0.92);
  const pageEnd = darken(accentColor, 0.86);
  const surface = mix(pageCenter, accentColor, 0.08);
  const surfaceAlt = mix(pageCenter, accentColor, 0.12);
  const surfaceElevated = mix(pageCenter, accentColor, 0.16);
  const border = mix(accentColor, [255, 255, 255], 0.22);
  const textSecondary = mix(accentColor, [255, 255, 255], 0.58);

  return {
    pageBackgroundStart: rgbToHex(...pageStart),
    pageBackground: rgbToHex(...pageCenter),
    pageBackgroundEnd: rgbToHex(...pageEnd),
    surface: rgbToHex(...surface),
    surfaceAlt: rgbToHex(...surfaceAlt),
    surfaceElevated: rgbToHex(...surfaceElevated),
    border: rgbToHex(...border),
    accent: rgbToHex(...accentColor),
    accentStrong: rgbToHex(...accentStrong),
    accentContrast: "#08101f",
    textPrimary: "#f5f7ff",
    textSecondary: rgbToHex(...textSecondary),
    gridLine: "rgba(255,255,255,0.018)",
    glowPrimary: rgba(accentColor[0], accentColor[1], accentColor[2], 0.20),
    glowSecondary: rgba(accentStrong[0], accentStrong[1], accentStrong[2], 0.14),
    badgeBackground: rgbToHex(...badge),
    badgeText: "#08101f",
    tabActiveBackground: rgba(accentStrong[0], accentStrong[1], accentStrong[2], 0.28),
    tabActiveText: "#f5f7ff",
    inputBackground: rgbToHex(...surfaceElevated),
    secondaryButtonBackground: rgbToHex(...surface),
    secondaryButtonText: "#f5f7ff",
    secondaryButtonBorder: rgbToHex(...border),
    skeletonBase: rgbToHex(...mix(surface, accentColor, 0.1)),
    skeletonHighlight: rgbToHex(...mix(surfaceElevated, accentStrong, 0.18)),
  };
};

export const createLightPaletteFromAccent = (
  accentColor: [number, number, number],
): ShowcasePalette => {
  const accentStrong = darken(accentColor, 0.08);
  const pageStart = lighten(accentColor, 0.92);
  const pageCenter = lighten(accentColor, 0.95);
  const pageEnd = lighten(accentColor, 0.9);
  const surface = "#ffffff";
  const surfaceAlt = rgbToHex(...lighten(accentColor, 0.96));
  const surfaceElevated = rgbToHex(...lighten(accentColor, 0.985));
  const border = rgbToHex(...mix(accentColor, [255, 255, 255], 0.72));
  const textPrimary = rgbToHex(...darken(accentColor, 0.76));
  const textSecondary = rgbToHex(...mix(accentColor, [80, 84, 103], 0.58));

  return {
    pageBackgroundStart: rgbToHex(...pageStart),
    pageBackground: rgbToHex(...pageCenter),
    pageBackgroundEnd: rgbToHex(...pageEnd),
    surface,
    surfaceAlt,
    surfaceElevated,
    border,
    accent: rgbToHex(...accentColor),
    accentStrong: rgbToHex(...accentStrong),
    accentContrast: "#ffffff",
    textPrimary,
    textSecondary,
    gridLine: "rgba(16,24,40,0.04)",
    glowPrimary: rgba(accentColor[0], accentColor[1], accentColor[2], 0.12),
    glowSecondary: rgba(accentStrong[0], accentStrong[1], accentStrong[2], 0.08),
    badgeBackground: rgbToHex(...accentColor),
    badgeText: "#ffffff",
    tabActiveBackground: rgbToHex(...lighten(accentColor, 0.82)),
    tabActiveText: textPrimary,
    inputBackground: "#ffffff",
    secondaryButtonBackground: "#ffffff",
    secondaryButtonText: textPrimary,
    secondaryButtonBorder: border,
    skeletonBase: rgbToHex(...lighten(accentColor, 0.9)),
    skeletonHighlight: "#ffffff",
  };
};

export const BRAND_THEME_PRESETS: PalettePreset[] = [
  {
    id: "midnight",
    name: "Midnight Glass",
    description: "Темная витрина с холодным премиальным акцентом.",
    tone: "dark",
    palette: createDarkPaletteFromAccent([155, 173, 222]),
  },
  {
    id: "ember",
    name: "Ember Copper",
    description: "Теплая тёмная схема с медным свечением.",
    tone: "dark",
    palette: createDarkPaletteFromAccent([209, 137, 84]),
  },
  {
    id: "emerald",
    name: "Emerald Depth",
    description: "Глубокий графит с изумрудным акцентом.",
    tone: "dark",
    palette: createDarkPaletteFromAccent([71, 178, 148]),
  },
  {
    id: "royal",
    name: "Royal Plum",
    description: "Более драматичная схема для luxury-настроения.",
    tone: "dark",
    palette: createDarkPaletteFromAccent([164, 118, 214]),
  },
  {
    id: "linen",
    name: "Linen Gold",
    description: "Светлая витрина с мягким тёплым акцентом.",
    tone: "light",
    palette: createLightPaletteFromAccent([193, 142, 79]),
  },
  {
    id: "skyline",
    name: "Skyline Blue",
    description: "Чистая светлая схема с деловым синим акцентом.",
    tone: "light",
    palette: createLightPaletteFromAccent([84, 121, 214]),
  },
  {
    id: "mint",
    name: "Mint Paper",
    description: "Лёгкая светлая тема с холодным мятным тоном.",
    tone: "light",
    palette: createLightPaletteFromAccent([67, 168, 154]),
  },
];
